/**
 * Self-contained local-date helpers — for the habits recurrence/streak engines
 * AND the task recurrence engine (`lib/recurrence/`).
 *
 * Deliberately dependency-free (native `Date`, no date-fns) and pure, so both
 * engines stay unit-testable. Lives in `lib/` because features must not import
 * each other; `features/habits/lib/date.ts` re-exports from here.
 * All operations work in the user's LOCAL timezone — a "day" is a local
 * calendar day, and the day rolls over at local midnight.
 */
import type { Weekday } from '@/src/renderer/lib/db/models'

const MS_PER_DAY = 86_400_000

/** Local 'YYYY-MM-DD' key for a date (timezone-safe, no UTC shift). */
export function dateKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Local midnight of the given date. */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** Parse a 'YYYY-MM-DD' key into a local-midnight Date. */
export function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** Monday-first weekday: Mo=0 … So=6. */
export function weekday(d: Date): Weekday {
  return ((d.getDay() + 6) % 7) as Weekday
}

/** A new date `n` local days after `d` (n may be negative). */
export function addDays(d: Date, n: number): Date {
  const r = startOfDay(d)
  r.setDate(r.getDate() + n)
  return r
}

/** Whole local days from `a` to `b` (b − a). DST-safe via rounding. */
export function daysBetween(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / MS_PER_DAY)
}

/** Number of days in the month containing `d`. */
export function daysInMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
}

/** Clamp a desired day-of-month to the actual length of `d`'s month
 *  (e.g. the 31st becomes 28/29 in February). */
export function clampDayOfMonth(d: Date, desiredDay: number): number {
  return Math.min(desiredDay, daysInMonth(d))
}

/** Local midnight of the Monday starting the week containing `d`. */
export function startOfWeek(d: Date): Date {
  return addDays(d, -weekday(d))
}

/** Whole Mon–So weeks between `a` and `b` (by week-boundary, b − a). */
export function weeksBetween(a: Date, b: Date): number {
  return Math.round(daysBetween(startOfWeek(a), startOfWeek(b)) / 7)
}

/** Whole calendar months between `a` and `b` (b − a). */
export function monthsBetween(a: Date, b: Date): number {
  return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth())
}

/** True if `a` and `b` are the same local calendar day. */
export function isSameDay(a: Date, b: Date): boolean {
  return dateKey(a) === dateKey(b)
}

/**
 * Der `nth`-te Wochentag im Monat `monthIndex` (0-basiert, darf überlaufen) —
 * `nth: -1` ist der letzte. Wochentag 0 = Mo … 6 = So, wie `weekday()`.
 */
export function nthWeekdayInMonth(year: number, monthIndex: number, nth: number, day: Weekday): Date {
  const first = new Date(year, monthIndex, 1)
  if (nth === -1) {
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0)
    return addDays(last, -((weekday(last) - day + 7) % 7))
  }
  return addDays(first, (day - weekday(first) + 7) % 7 + (nth - 1) * 7)
}

/** Welcher der wievielte Wochentag `d` in seinem Monat ist; ab dem 5. zählt er als letzter. */
export function nthOfWeekday(d: Date): 1 | 2 | 3 | 4 | -1 {
  const n = Math.ceil(d.getDate() / 7)
  return n >= 5 ? -1 : (n as 1 | 2 | 3 | 4)
}

/** The `day`-th day of month `monthIndex` (0-based, may overflow) in `year`,
 *  clamped to the month's length: (2026, 1, 31) → 28 February 2026. */
export function dateInMonth(year: number, monthIndex: number, day: number): Date {
  const first = new Date(year, monthIndex, 1)
  return new Date(first.getFullYear(), first.getMonth(), clampDayOfMonth(first, day))
}
